"use client";

import { useApp } from "@/context/AppContext";
import { useMemo, useState } from "react";
import {
  isToday,
  startOfMonth,
  startOfWeek,
  endOfWeek,
  endOfMonth,
  eachDayOfInterval,
  format,
  isSameDay,
  subDays,
} from "date-fns";
import { useNavigate } from "react-router-dom";
import CommonHeader from "./common/CommonHeader";
import { MetricsGrid } from "./dashboard/MetricsGrid";
import { RevenueChart } from "./dashboard/RevenueChart";
import { RecentActivities } from "./dashboard/RecentActivities";
import { UpcomingAppointments } from "./dashboard/UpcomingAppointments";
import { TodayBookingsCard } from "./dashboard/TodayBookingsCard";
import { StatusBreakdownCard } from "./dashboard/StatusBreakdownCard";
import { CashByTeamCard } from "./dashboard/CashByTeamCard";
import { MonthlySalesChart } from "./dashboard/MonthlySalesChart";
import { RevenueChartData } from "@/services/invoiceService";
import {
  TodayStats,
  CashByTeam,
  TodayRevenue,
  MonthlySalesData,
} from "@/services/dashboardService";

export function Dashboard() {
  const navigate = useNavigate();
  const {
    appointments,
    customers,
    subscriptions,
    invoices,
    activities,
    vehicles,
    staff,
  } = useApp();

  // State for charts
  const [salesPeriod, setSalesPeriod] = useState<"weekly" | "monthly">(
    "monthly",
  );

  // Calculate all metrics from context data
  const dashboardData = useMemo(() => {
    const today = new Date();
    const todayAppointments = appointments.filter((apt) =>
      isToday(new Date(apt.date)),
    );

    // --- 1. Today's Revenue ---
    // Collected: Payments made today (from paid invoices with today's date)
    const todayPaidInvoices = invoices.filter(
      (inv) => isToday(new Date(inv.date)) && inv.paymentStatus === "paid",
    );
    const collectedRevenue = todayPaidInvoices.reduce(
      (sum, inv) => sum + inv.total,
      0,
    );

    // Pending: Pending invoices for today
    const todayPendingInvoices = invoices.filter(
      (inv) => isToday(new Date(inv.date)) && inv.paymentStatus === "pending",
    );
    const pendingRevenue = todayPendingInvoices.reduce(
      (sum, inv) => sum + inv.total,
      0,
    );

    // Total Expected: Sum of all today's appointments cost
    const totalExpected = todayAppointments.reduce(
      (sum, apt) => sum + (apt.amount || 0),
      0,
    );

    const todayRevenue: TodayRevenue = {
      collected: collectedRevenue,
      pending: pendingRevenue,
      totalExpected,
    };

    // --- 2. Today's Stats (Bookings & Status) ---
    const bookingsByType = {
      online: 0,
      offline: 0,
      subscription: 0,
    };

    const statusCounts = {
      completed: 0,
      assigned: 0,
      inProgress: 0,
      notCompleted: 0,
      postponed: 0,
      cancelled: 0,
      pending: 0,
      confirmed: 0,
    };

    todayAppointments.forEach((apt) => {
      // Count by type
      if (apt.bookingType === "ONLINE") bookingsByType.online++;
      else if (apt.bookingType === "SUBSCRIPTION")
        bookingsByType.subscription++;
      else bookingsByType.offline++; // Default to offline for others

      // Count by status
      const status = apt.status?.toLowerCase();
      if (status === "completed") statusCounts.completed++;
      else if (status === "in_progress" || status === "in-progress")
        statusCounts.inProgress++;
      else if (status === "not_completed" || status === "not-completed")
        statusCounts.notCompleted++;
      else if (status === "postponed") statusCounts.postponed++;
      else if (status === "cancelled") statusCounts.cancelled++;
      else if (status === "pending" || status === "confirmation-pending")
        statusCounts.pending++;
      else if (status === "confirmed") statusCounts.confirmed++;
      // 'Assigned' logic depends on your business rule, often 'confirmed' with staff assigned
      if ((status === "confirmed" || status === "pending") && apt.staffId) {
        statusCounts.assigned++;
      }
    });

    const todayStats: TodayStats = {
      totalBookings: todayAppointments.length,
      bookingsByType,
      statusCounts,
    };

    // --- 3. Cash By Team ---
    const staffPerformance: Record<string, CashByTeam> = {};

    // Initialize for all active staff
    staff.forEach((s) => {
      staffPerformance[s.id] = {
        staffId: s.id,
        staffName: s.name,
        orders: 0,
        amount: 0,
      };
    });

    // Aggregate from today's completed appointments or paid invoices
    // Using appointments for job count
    todayAppointments.forEach((apt) => {
      if (apt.staffId && staffPerformance[apt.staffId]) {
        staffPerformance[apt.staffId].orders++;
        if (apt.status === "COMPLETED") {
          // For amount, better to check specific invoices or use appointment amount
          // aggregating appointment amount for simplicity
          staffPerformance[apt.staffId].amount += apt.amount || 0;
        }
      }
    });

    const cashByTeam = Object.values(staffPerformance).filter(
      (item) => item.orders > 0 || item.amount > 0,
    );

    // --- 4. Monthly/Weekly Sales Chart Data ---
    let intervalStart: Date;
    let intervalEnd: Date;
    let dateFormat: string;

    if (salesPeriod === "weekly") {
      intervalStart = startOfWeek(today, { weekStartsOn: 1 }); // Monday start
      intervalEnd = endOfWeek(today, { weekStartsOn: 1 });
      dateFormat = "yyyy-MM-dd";
    } else {
      intervalStart = startOfMonth(today);
      intervalEnd = endOfMonth(today);
      dateFormat = "yyyy-MM-dd";
    }

    const intervalDays = eachDayOfInterval({
      start: intervalStart,
      end: intervalEnd,
    });

    const salesData: MonthlySalesData[] = intervalDays.map((day) => {
      // Find invoices for this day
      const dayInvoices = invoices.filter((inv) =>
        isSameDay(new Date(inv.date), day),
      );
      // Find completed appointments for this day (for car count)
      const dayAppointments = appointments.filter(
        (apt) =>
          isSameDay(new Date(apt.date), day) &&
          (apt.status === "COMPLETED" || apt.status === "completed"),
      );

      return {
        date: format(day, dateFormat),
        revenue: dayInvoices.reduce((sum, inv) => sum + inv.total, 0),
        count: dayAppointments.length,
      };
    });

    // --- 5. Revenue Chart Data (Last 7 days) ---
    const last7Days = eachDayOfInterval({
      start: subDays(today, 6),
      end: today,
    });

    const revenueChartData: RevenueChartData[] = last7Days.map((day) => {
      const dayName = format(day, "EEE"); // Mon, Tue, etc.
      const dayInvoices = invoices.filter((inv) =>
        isSameDay(new Date(inv.date), day),
      );
      const total = dayInvoices.reduce((sum, inv) => sum + inv.total, 0);
      return { date: dayName, revenue: total };
    });

    // --- 6. Other Metrics ---
    const activeSubsCount = subscriptions.filter(
      (sub) => sub.status === "active",
    ).length;

    // Upcoming appointments (pending/confirmed in future)
    // Already sorted in backend usually, but here we filter/sort locally
    const upcoming = appointments
      .filter((apt) => {
        const aptDate = new Date(apt.date);
        return (
          aptDate > today &&
          (apt.status === "PENDING" || apt.status === "CONFIRMED")
        );
      })
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
      .slice(0, 10);

    return {
      todayRevenue,
      todayStats,
      cashByTeam,
      salesData,
      revenueChartData,
      activeSubsCount,
      upcoming,
      totalCustomers: customers.length,
      vehiclesCount: vehicles.length,
    };
  }, [
    appointments,
    customers,
    subscriptions,
    invoices,
    vehicles,
    staff,
    salesPeriod,
  ]);

  // Helper for status breakdown dictionary for MetricsGrid
  // (MetricsGrid expects Record<string, number> for 'scheduled' etc.)
  const todayStatsDict: Record<string, number> = {
    scheduled: dashboardData.todayStats.totalBookings,
    ...dashboardData.todayStats.statusCounts,
  };

  return (
    <div className="space-y-8 max-w-[1600px]">
      {/* Header */}
      <CommonHeader
        title="Dashboard"
        description="Welcome to WeClean Car Wash Management"
        onClick={() => navigate("/appointments")}
        buttonText="New Appointment"
      />

      {/* Metrics Grid */}
      <MetricsGrid
        todayRevenue={dashboardData.todayRevenue}
        todayAppointments={dashboardData.todayStats.totalBookings}
        todayStats={todayStatsDict}
        totalCustomers={dashboardData.totalCustomers}
        vehiclesCount={dashboardData.vehiclesCount}
        activeSubscriptions={dashboardData.activeSubsCount}
      />

      {/* Today's Bookings and Status Breakdown */}
      <div className="grid gap-6 lg:grid-cols-2">
        <TodayBookingsCard stats={dashboardData.todayStats} loading={false} />
        <StatusBreakdownCard stats={dashboardData.todayStats} loading={false} />
      </div>

      {/* Monthly Sales Chart and Cash by Team */}
      <div className="grid gap-6 lg:grid-cols-7">
        <MonthlySalesChart
          data={dashboardData.salesData}
          period={salesPeriod}
          onPeriodChange={setSalesPeriod}
          loading={false}
        />
        <div className="lg:col-span-3">
          <CashByTeamCard data={dashboardData.cashByTeam} loading={false} />
        </div>
      </div>

      {/* Revenue Chart and Recent Activities */}
      <div className="grid gap-6 lg:grid-cols-7">
        <RevenueChart data={dashboardData.revenueChartData} />
        <RecentActivities activities={activities} />
      </div>

      {/* Upcoming Appointments */}
      <UpcomingAppointments
        appointments={dashboardData.upcoming}
        customers={customers}
        vehicles={vehicles}
        staff={staff}
      />
    </div>
  );
}
